import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Notification,
  Logging,
  Certificate,
  DropZone,
  FileType,
  DropZoneProps,
  Image,
  API,
  NotificationData,
  ResponseError,
  getUuidFromLocationHeader,
  S3Uploader,
  S3UploadRequest,
  S3UploadResponse,
  S3UploadFile,
  getUuidV4,
  S3UploaderStatus,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxIcon, VtxStep, VtxSteps } from '@vertexinc/vtx-ui-react-component-library';
import AddCertificateProps from './AddCertificateProps.interface';
import CertificateInformation from './Steps/CertificateInformation/CertificateInformation';
import './add-certificate.scss';
import './Steps/steps.scss';
import JurisdictionInformation from './Steps/JurisdictionInformation/JurisdictionInformation';
import ContactInformation from './Steps/ContactInformation/ContactInformation';
import AdditionalDocumentation from './Steps/AdditionalDocumentation/AdditionalDocumentation';
import DocumentViewer from '../DocumentViewer/DocumentViewer';
import useJurisdictions from './Common/useJurisdictions';
import SelectedItems from './Common/selectedItems';

const BYTES_PER_MEGABYTE = 1048576;
const BYTES_PER_GIGABYTE = BYTES_PER_MEGABYTE * 1024;

interface Step {
  key: number;
  title: JSX.Element;
  description?: string;
  content: JSX.Element;
}

const AddCertificate = ({
  gatewayEndPoint,
  username,
}: AddCertificateProps): JSX.Element => {
  const numberSteps = 4;
  const dispatch = useDispatch();

  // Empty Selected Items
  const emptySelectedItems: SelectedItems = {
    customer: null,
    seller: null,
    products: null,
    exemptionReason: null,
  };

  const [uploadUuid, setUploadUuid] = React.useState(null);
  const [uploadFiles, setUploadFiles] = React.useState(null);
  const [uploadFileNameUrlMap, setUploadFileNameUrlMap] = React.useState(null);

  const [saveInProgress, setSaveInProgress] = React.useState(false);
  const [redirect, setRedirect] = React.useState(null);
  const [certificate, setCertificate] = React.useState(null);

  // used to Store Previous Selections incase user goes back to previous Step
  const [selectedItems, setSelectedItems] = React.useState(emptySelectedItems);

  const [certificateImage, setCertificateImage] = React.useState(null);

  // The Certificate that will be save after the S3 File Upload completes
  const [pendingCertificate, setPendingCertificate] = React.useState(null);

  const [dropFiles, setDropFiles] = React.useState([]);
  const [statusMap, setStatusMap] = React.useState(new Map<string, boolean | string>());

  const [additionalDropFiles, setAdditionalDropFiles] = React.useState([]);
  const [additionalStatusMap, setAdditionalStatusMap] = React.useState(new Map<string, boolean | string>());

  const [filesError, setFilesError] = React.useState(null);

  const [selectedStep, setSelectedStep] = React.useState(0);
  const [notification, setNotification] = React.useState(null);

  // Load the Jurisdictions once in the Parent
  const [jurisdictions, jurisdictionsNotification] = useJurisdictions(gatewayEndPoint);

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  // Make the API Call to Add the Certificate
  const addCertificate = (newCertificate: Certificate) => async (): Promise<void> => {
    try {
      const copyCertificate: Certificate = _.cloneDeep(newCertificate);

      const api = new API(gatewayEndPoint);

      // New Contact was Configured
      if (copyCertificate.contact?.uuid === null) {
        // Add the Customer Contact
        const headers: Headers = await api.customer.addCustomerContactV41(copyCertificate.customer?.uuid, copyCertificate?.contact);
        const newContactUuid: string = getUuidFromLocationHeader(headers);

        Logging.Info('Add Contact', newContactUuid, copyCertificate?.contact);

        copyCertificate.contact.uuid = newContactUuid;
      }

      // Add the Certificate
      const headers: Headers = await api.certificate.addCertificateV41(copyCertificate);
      const newCertificateUuid: string = getUuidFromLocationHeader(headers);

      Logging.Info('Certificate Added', newCertificateUuid);

      setPendingCertificate(null);

      setSaveInProgress(false);

      // Direct back to Products
      setRedirect('/certificates');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Save Error',
        description: 'Unable to Add the Certificate.',
        error: error as ResponseError,
      };

      setNotification(notificationData);

      setSaveInProgress(false);
    }
  };

  // Make the API Call to Upload the Certificate Files
  const uploadCertificateFiles = (newCertificate: Certificate) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Create the Request
      const s3UploadRequest: S3UploadRequest = {
        username,
        certificateImageFile: dropFiles.length === 1
          ? {
            name: dropFiles[0].name,
            contentType: dropFiles[0].type,
          }
          : null,
        additionalDocumentationFiles: additionalDropFiles?.length > 0
          ? additionalDropFiles?.map((file: File) => { // eslint-disable-line arrow-body-style
            return {
              name: file.name,
              contentType: file.type,
            };
          })
          : null,
      };

      Logging.Info('Requesting Certificate Urls', s3UploadRequest);

      // Request Presigned Urls if any Images need to be Uploaded
      // const s3UploadResponse: S3UploadResponse = await api.certificate.certificateS3UploadV41(s3UploadRequest); // FIX ME

      const dummyUrl = 'https://s3.console.aws.amazon.com/s3/buckets/vertexinc-ecm-dev-bulk-us-east-1?region=us-east-1&prefix=sample0414/';

      const s3UploadResponse: S3UploadResponse = {
        uuid: null,
        certificateImageFile: s3UploadRequest.certificateImageFile
          ? {
            name: s3UploadRequest.certificateImageFile.name,
            contentType: s3UploadRequest.certificateImageFile.contentType,
            url: dummyUrl,
          }
          : null,
        additionalDocumentationFiles: s3UploadRequest.additionalDocumentationFiles?.map((s3File: S3UploadFile) => { // eslint-disable-line arrow-body-style, max-len
          return {
            name: s3File.name,
            contentType: s3File.contentType,
            url: dummyUrl,
          };
        }),
      };

      Logging.Info('Certificate Urls', s3UploadResponse);

      const files: File[] = [];
      const fileNameUrlMap = new Map<string, string>();

      // Clone the Certificate
      const copyCertificate: Certificate = _.cloneDeep(newCertificate);

      // Initialize the Certificate Image
      copyCertificate.certificateImage = null;

      // Certificate Image specified
      if (dropFiles.length === 1) {
        const file: File = dropFiles[0];

        // Get the presigned Url from the Response
        const s3File: S3UploadFile = s3UploadResponse.certificateImageFile;

        if (s3File) {
          files.push(file);
          fileNameUrlMap.set(file.name, s3File.url);

          // Update the Certificate
          copyCertificate.certificateImage = {
            name: file.name,
            image: null,
            contentType: file.type,
            location: s3File.url,
          };
        } else {
          Logging.Error('Cannot find S3 File:', file.name, s3UploadResponse);
        }
      }

      // Initialize the Additional Documentation
      copyCertificate.supportingDocumentationImages = (additionalDropFiles.length > 0) ? [] : null;

      // Additional Documentation specified
      additionalDropFiles?.forEach((file: File) => {
        // Get the presigned Url from the Response
        const s3File: S3UploadFile = s3UploadResponse.additionalDocumentationFiles.filter((uploadFile: S3UploadFile) => uploadFile.name === file.name)[0]; // eslint-disable-line max-len

        if (s3File) {
          files.push(file);
          fileNameUrlMap.set(file.name, s3File.url);

          // Update the Certificate
          copyCertificate.supportingDocumentationImages.push({
            name: file.name,
            image: null,
            contentType: file.type,
            location: s3File.url,
          });
        } else {
          Logging.Error('Cannot find S3 File:', file.name, s3UploadResponse);
        }
      });

      setUploadFileNameUrlMap(fileNameUrlMap);
      setUploadFiles(files);

      // Save the Pending Certificate
      setPendingCertificate(copyCertificate);

      Logging.Info('Upload Starting');

      // Set Upload UUID- Triggers the Upload to Start
      setUploadUuid(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate File(s) Upload Error',
        description: 'Unable to Upload the Certificate File(s).',
        error: error as ResponseError,
      };

      setNotification(notificationData);

      setSaveInProgress(false);
    }
  };

  // Continue / Save Button
  const onContinueSave = (
    step: number,
    updatedCertificate: Certificate,
    items: SelectedItems,
  ): void => {
    Logging.Debug('onContinueSave', step, updatedCertificate, items);

    setCertificate(updatedCertificate);
    setSelectedItems(items);

    // More Steps to come
    if (selectedStep < (numberSteps - 1)) {
      setSelectedStep(selectedStep + 1);
    } else { // Final Step
      Logging.Info('Saving Certificate:', updatedCertificate);

      setSaveInProgress(true);

      // Certificate has Files
      if (dropFiles.length > 0 || additionalDropFiles.length > 0) {
        // Upload the Certificate Files
        dispatch(uploadCertificateFiles(updatedCertificate));
      } else {
        // Add the Certificate
        dispatch(addCertificate(updatedCertificate));
      }
    }
  };

  const onCancel = (): void => {
    Logging.Info('Cancel');

    // Direct to Certificates
    setRedirect('/certificates');
  };

  // Remove S3 Upload
  const removeUpload = (uuid: string): void => {
    // Remove S3 Upload : Success or Failure
    Logging.Info('Remove S3 Upload', uuid);

    // Clear the Progress Bar
    setUploadUuid(null);

    // Clear the Upload Files
    setUploadFileNameUrlMap(null);
    setUploadFiles(null);
  };

  // S3 Upload Status : Success or Failure
  const statusUpload = (uuid: string, status: S3UploaderStatus, reason: string): void => {
    // Upload Complete
    Logging.Info('S3 Upload Status', uuid, status, reason);

    if (status === S3UploaderStatus.FAILED) {
      // Abort the save
      setSaveInProgress(false);

      // Clear the Pending Certificate
      setPendingCertificate(null);

      // Do not Remove the Upload.  Will be rmoved when the Notification closes in the S3 Upload
    } else if (status === S3UploaderStatus.SUCCESS) {
      // Remove the Upload
      removeUpload(uuid);

      if (pendingCertificate) {
        // Add the Certificate
        dispatch(addCertificate(pendingCertificate));
      }
    }
  };

  // Step Selected in Header
  const onSelectStep = (step: number): void => {
    Logging.Info('onSelectStep', step, selectedStep);

    // Can only step backwards
    if ((saveInProgress === false) && (step < selectedStep)) {
      // Do not Change the Steps here as it will bypass the validation
      setSelectedStep(step);
    }
  };

  // Delete the Certificate Image - Optional
  const onDeleteCertificateImage = (image: Image): void => {
    setDropFiles([]);
    setStatusMap(new Map<string, boolean | string>());
    setCertificateImage(null);
  };

  // Set the Error Notification
  React.useEffect(() => {
    setNotification(jurisdictionsNotification);
  }, [jurisdictionsNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Add Certificate: Mount');

    setCertificate(new Certificate());
    return (): void => {
      setRedirect(null);
    };
  }, []);

  React.useEffect(() => {
    if (dropFiles && statusMap && dropFiles.length && statusMap.size) {
      const certificateFile: File = dropFiles[0];
      const certificateFileStatus: boolean | string = statusMap.get(certificateFile.name);

      // File was successfully loaded
      if (certificateFileStatus === true) {
        const image: Image = {
          name: certificateFile.name,
          image: null,
          contentType: certificateFile.type,
          location: null,
          file: certificateFile,
        };

        setCertificateImage(image);
      }
    }
  }, [dropFiles, statusMap]);

  const steps: Step[] = [
    {
      key: 1,
      title: <>Certificate<br />Information</>,
      description: 'Certificate Information',
      // eslint-disable-next-line max-len
      content: <CertificateInformation gatewayEndPoint={gatewayEndPoint} step={0} onCancel={onCancel} onContinuteSave={onContinueSave} certificate={certificate} selectedItems={selectedItems} />,
    },
    {
      key: 2,
      title: <>Jurisdiction<br />Information</>,
      description: 'Jurisdiction Information',
      // eslint-disable-next-line max-len
      content: <JurisdictionInformation gatewayEndPoint={gatewayEndPoint} step={1} onCancel={onCancel} onContinuteSave={onContinueSave} certificate={certificate} selectedItems={selectedItems} jurisdictions={jurisdictions} />,
    },
    {
      key: 3,
      title: <>Contact<br />Information</>,
      description: 'Contact Information',
      // eslint-disable-next-line max-len
      content: <ContactInformation gatewayEndPoint={gatewayEndPoint} step={2} onCancel={onCancel} onContinuteSave={onContinueSave} certificate={certificate} selectedItems={selectedItems} />,
    },
    {
      key: 4,
      title: <>Additional<br />Documentation</>,
      description: 'Additional Documentation',
      // eslint-disable-next-line max-len
      content: <AdditionalDocumentation gatewayEndPoint={gatewayEndPoint} step={3} onCancel={onCancel} onContinuteSave={onContinueSave} certificate={certificate} selectedItems={selectedItems} dropFiles={additionalDropFiles} setDropFiles={setAdditionalDropFiles} statusMap={additionalStatusMap} setStatusMap={setAdditionalStatusMap} saveInProgress={saveInProgress} />,
    },
  ];

  // Certificate Drop Zone Parameters
  const certificateDropProps: DropZoneProps = {
    dragTextDescription: 'Drag the Certificate Image file here, or click to select file.',
    dropTextDescription: 'Drop the Certificate Image file here.',
    showRestrictions: true,
    maxFileSize: BYTES_PER_MEGABYTE * 30, // 30MB
    maxFilesSize: null,
    maxNumberFiles: 1,
    allowedFileTypes: [
      FileType.Pdf,
      FileType.Png,
      FileType.Gif,
      FileType.Jpg,
    ],
    dragDropImage: '/images/drag-drop-csv.png',
    dropFiles,
    setDropFiles,
    statusMap,
    setStatusMap,
    setFilesError,
    virusScanningUrl: 'https://virus-scanning-service-dev.dev.vtxdev.net/v1/syncScan/file',
  };

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  return (
    <>
      <div className="addCertificateContainer">
        <div className="addCertificateContainerLeft">
          <div className="addCertificateHeader">
            <div className="addCertificateHeaderLeft">
              <div className="addCertificateHeaderBack">
                <VertexLink href="#/certificates" display="Back to Certificates" />
              </div>
              <div className="addCertificateHeaderTitle">
                Add Certificate
              </div>
            </div>
            <div className="addCertificateHeaderRight">
              {
                (uploadUuid || saveInProgress) && ( // FIX ME
                  <S3Uploader
                    gatewayEndPoint={gatewayEndPoint}
                    uuid={uploadUuid}
                    files={uploadFiles}
                    fileNameUrlMap={uploadFileNameUrlMap}
                    removeUpload={removeUpload}
                    statusUpload={statusUpload}
                    progressWidth={60}
                  />
                )
              }
            </div>
          </div>
          <div className="certificateImageHeader">
            Certificate Image
          </div>
          <div className="certificateImage">
            {
              certificateImage
                ? (
                  <div className="certificateImageViewer">
                    <DocumentViewer
                      idPrefix="cert"
                      canRotate={certificateImage.contentType !== FileType.Pdf}
                      canDelete
                      onDelete={onDeleteCertificateImage}
                      imageDocument={certificateImage}
                      additionalControlsStyle="certificateImageViewerControlsVertical"
                    />
                  </div>
                )
                : (
                  <>
                    <DropZone {...certificateDropProps} />
                    {
                      filesError
                      && (
                        <div className="dropZoneFooter">
                          <VtxIcon type="exclamation-circle" fontSize={20} color="red" className="dropZoneFooterIcons" />
                          <span>{filesError}</span>
                        </div>
                      )
                    }
                  </>
                )
            }
          </div>
        </div>
        <div className="addCertificateContainerRight">
          <div className="addCertificateContent addCertificateStepsContainer">
            <VtxSteps current={selectedStep} onChange={onSelectStep} size="small">
              {steps.map((step: Step, index: number) => (
                <VtxStep key={step.key} title={step.title} />))}
            </VtxSteps>
            <hr />
            <div className="stepContainer">
              {steps[selectedStep].content}
            </div>
          </div>
        </div>
      </div>
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default AddCertificate;
